import { Box, Typography, styled } from '@mui/material'
import { MButton, ProgressLoaders, Text } from 'components'
import { MESSAGES, USER_TOKEN } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { formatValue, useLocalStorage } from 'utils'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { NoData } from './NoData'
import { BillItem, BillingHeader, StyledScrollbar } from './PaymentHistory'
import { useSnackbar } from 'notistack'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { renderDateFilter } from 'containers/Usage/components/UsageViewGraph'
import { isNil } from 'lodash'
import Tooltip from '@mui/material/Tooltip'
import { type BillingData } from 'utils/hooks/types'

export const BillingHistory = (): JSX.Element => {
  const { billing, getBillingURL } = useAuth()
  const isMobile = useMobileBreakpoints()
  const { account, currentAccount, getBilling, isAdmin, userEmail } =
    useAuth()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  // const [prevAccountId, setPrevAccountId] = useState<string>('')
  const { enqueueSnackbar } = useSnackbar()
  const [year, setYear] = useState<string>(dayjs().format('YYYY'))

  const handleDateFilterChange = (event: any): void => {
    setYear(event.target.value)
  }

  useEffect(() => {
    // Skip only if we don't have a current account
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!currentAccount?.accountId && !account?.myAccount?.accountId) {
      return
    }
    void getBilling({
      AccessToken: authToken,
      accountId: currentAccount?.accountId ?? account?.myAccount.accountId,
      startDate: dayjs('01-01-' + year).format('YYYY-MM-DD'),
      endDate: dayjs('12-31-' + year).format('YYYY-MM-DD'),
      admin: isAdmin,
      email: userEmail
    })
    // setPrevAccountId((currentAccount?.accountId ?? account?.myAccount?.accountId) ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount, userEmail, isAdmin, authToken, year])


  const handleBillClick = async (billId: string): Promise<void> => {
    try {
      const response = await getBillingURL({
        email: userEmail,
        billId,
        isAdmin,
        AccessToken: authToken
      })

      const { onlineBillURL, billDoesNotExists } = response?.data as { onlineBillURL: string, billDoesNotExists: string }

      if (billDoesNotExists) {
        enqueueSnackbar(billDoesNotExists || MESSAGES.BILL_PDF_ERROR, {
          variant: 'error',
          autoHideDuration: 3000
        })
      }

      if (!isNil(onlineBillURL) && onlineBillURL !== '') {
        window.open(onlineBillURL, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.log(error)
    }
  }

  const isBillValid = (bill: BillingData): boolean => {
    // Del-Co went live with CCS in February of 2025
    // https://esc-partners.atlassian.net/browse/SDW-112?focusedCommentId=241586
    const minDate = new Date('2025-02-01')
    const d = new Date(bill.billDate)
    return !isNaN(d.getTime()) && d >= minDate
  }

  // Generate year options dynamically from 2024 to current year
  const generateYearOptions = (): string[] => {
    const currentYear = dayjs().year()
    const startYear = 2024
    const years: string[] = []
    for (let year = currentYear; year >= startYear; year--) {
      years.push(String(year))
    }
    return years
  }

  return (
    <Wrapper>
      {billing !== null
        ? <>
          {isMobile
            ? <>
              {billing?.billing.length === 0 && (
                <NoData transactionType="billing" />
              )}
              {billing?.billing.length !== 0 && billing?.billing.slice().reverse().map((bill, index) => {
                const isDisabled = !isBillValid(bill)
                return <BillingEntryMobile key={index} date={bill.billDate} amount={bill.billAmount} isDisabled={isDisabled} onViewBill={async () => { await handleBillClick(bill.billId) }} />
              })}
            </>
            : <Box >
              <Box p={3}>
                <Typography variant="h2" color={color.grey[900]} fontSize={isMobile ? 18 : 24} fontWeight={600}>
                  Billing History
                </Typography>
              </Box>
              <Box ml={3} mb={0.5}>
                {renderDateFilter(
                  'monthly',
                  year,
                  handleDateFilterChange,
                  generateYearOptions()
                )}
              </Box>
              {billing?.billing.length === 0 && (
                <NoData transactionType="billing" />
              )}
              {billing?.billing.length !== 0 && <>
                <BillingHeader>
                  <ItemWrapper>Bill Date</ItemWrapper>
                  <ItemWrapper>Bill Amount</ItemWrapper>
                  <ItemWrapper>Bill Print File</ItemWrapper>
                </BillingHeader>
                <StyledScrollbar maxHeight="350px" overflow="auto">
                  {billing?.billing.slice().reverse().map((bill, index) => {
                    const isDisabled = !isBillValid(bill)
                    return (
                      <BillItem key={index}>
                        <ItemWrapper small="true">{dayjs(bill.billDate).format('MM-DD-YYYY')}</ItemWrapper>
                        <ItemWrapper small="true">{formatValue(bill.billAmount)}</ItemWrapper>
                        <ItemWrapper>
                          <Tooltip
                            title="Unable to view bill for this date."
                            placement="top-end"
                            disableHoverListener={!isDisabled}
                            disableFocusListener={!isDisabled}
                            disableTouchListener={!isDisabled}
                          >
                            <Box display="flex" alignItems="center">
                              <MButton disabled={isDisabled} texttransform='none' onClick={async () => { await handleBillClick(bill.billId) }}>
                                <Text fontSize="14px" color={color.grey[900]} margin="0 10px 0 0">
                                  View Bill
                                </Text>
                                <PictureAsPdfIcon />
                              </MButton>
                            </Box>
                          </Tooltip>
                        </ItemWrapper>
                      </BillItem>
                    )
                  }
                  )}
                </StyledScrollbar>
              </>}
            </Box>}
        </>
        : <ProgressLoaders height="100%" />
      }
    </Wrapper >
  )
}

export const Wrapper = styled(Box)({
  background: color.white,
  padding: '24px 20px',
  marginBottom: '10px'
})

export const ItemWrapper = styled(Box)<{ small?: string }>(({ small }) => ({
  width: '25%',
  color: color.black,
  fontSize: small === 'true' ? '14px' : '16px',
  fontWeight: small === 'true' ? '400' : 'bold',
  display: 'flex',
  alignItems: 'center',
  gap: 5
}))

interface BillingEntryMobileProps {
  date: string
  amount: string
  isDisabled: boolean
  onViewBill: () => Promise<void>
}

const BillingEntryMobile: React.FC<BillingEntryMobileProps> = (props: BillingEntryMobileProps) => {
  return <Box py={2} borderBottom={`solid 1px ${color.background[50]}`}>
    <Box display="flex" justifyContent="space-between">
      <Typography fontSize={14} color={color.grey[700]}>Bill Date</Typography>
      <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>{dayjs(props.date).format('MM-DD-YYYY')}</Typography>
    </Box>
    <Box display="flex" justifyContent="space-between">
      <Typography fontSize={14} color={color.grey[700]}>Payment Amount</Typography>
      <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>{formatValue(props.amount)}</Typography>
    </Box>
    <Box display="flex" justifyContent="space-between">
      <Typography fontSize={14} color={color.grey[700]}>Bill Print File</Typography>
      <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>
        <MButton padding="0px" texttransform='none' disabled={props.isDisabled} onClick={() => { void props.onViewBill() }}>
          <u>View Bill</u>
        </MButton>
      </Typography>
    </Box>
  </Box>
}
